import { FC, useState } from "react";
import { useAlertStore, usePopupStore, useThemeStore } from "../../../stores";
import {
  Bank,
  Cheque,
  ChequeStatus,
  ChequeStatusType,
  Timestamp,
} from "../../../types";
import {
  ChequeSearchProperties,
  ChequeSearchSetters,
} from "../../../components/cheque/cheque-search-form/types";
import { ChequeDetails, ChequeSearchForm } from "../../../components/cheque";
import { ChequeApi } from "../../../api";
import {
  addComma,
  chequeStatusToString,
  getDateStr,
  getRangeLastMonth,
} from "../../../utils";
import { Column } from "../../../components/containers";
import { Heading, Text } from "../../../components/typography";
import { AlignItems } from "../../../components/containers/types";
import Table from "../../../components/table";
import { TableRendererProps } from "../../../components/table/types";

const INITIAL_CHEQUE: ChequeSearchProperties = {
  name: "",
  account_no: "",
  bank: null,
  number: "",
  price: 0,
  shenase: "",
  fromDate: getRangeLastMonth().from,
  toDate: getRangeLastMonth().to,
  confirmedWith: "",
  status: null,
};

const SearchCheque: FC = () => {
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const theme = useThemeStore((s) => s.theme);

  const [cheque, setCheque] = useState<ChequeSearchProperties>(INITIAL_CHEQUE);

  const getSetters = (): ChequeSearchSetters => {
    return {
      setAccountNo: (v) =>
        setCheque((prev) => ({ ...prev, account_no: v as string })),
      setBank: (v) =>
        setCheque((prev) => ({ ...prev, bank: v as Bank | null })),
      setName: (v) => setCheque((prev) => ({ ...prev, name: v as string })),
      setNumber: (v) => setCheque((prev) => ({ ...prev, number: v as string })),
      setPrice: (v) => setCheque((prev) => ({ ...prev, price: v as number })),
      setShenase: (v) =>
        setCheque((prev) => ({ ...prev, shenase: v as string })),
      setFromDate: (v) =>
        setCheque((prev) => ({ ...prev, fromDate: v as Timestamp })),
      setToDate: (v) =>
        setCheque((prev) => ({ ...prev, toDate: v as Timestamp })),
      setConfirmedWith: (v) =>
        setCheque((prev) => ({ ...prev, confirmedWith: v as string })),
      setStatus: (v) =>
        setCheque((prev) => ({
          ...prev,
          status: v as ChequeStatusType | null,
        })),
    };
  };

  const [cheques, setCheques] = useState<Array<Cheque>>([]);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  const onSearch = (): void => {
    setCheques([]);
    ChequeApi.search(cheque)
      .then((res) => {
        setCheques(res);
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      });
  };

  const findCheque = (part: any): Cheque[] => {
    const sc: Cheque[] = cheques.filter((c) => c._id.$oid === part._id.$oid);
    return sc;
  };

  return (
    <>
      <div
        style={{
          background: theme.overlay,
          borderRadius: 15,
          minHeight: 200,
          marginTop: "1rem",
          padding: "1rem",
        }}
      >
        <ChequeSearchForm
          {...cheque}
          {...getSetters()}
          buttons={["search"]}
          onSearch={onSearch}
        />
      </div>
      <Column
        style={{ minHeight: 70, marginTop: "1rem" }}
        alignItems={AlignItems.Start}
        gap={10}
      >
        <Heading size={18} color={theme.textOnBg}>
          نتایج
        </Heading>

        <div
          style={{
            overflowX: "auto",
            width: "calc(100% - 20px)",
            background: theme.overlay,
            padding: "20px 10px",
            borderRadius: 15,
          }}
        >
          <Table<{
            name: string;
            date: Timestamp;
            number: string;
            status: ChequeStatus;
            price: number;
          }>
            data={cheques}
            label={{
              name: "نام طرف حساب",
              date: "تاریخ",
              number: "شماره چک",
              status: "وضعیت",
              price: "مبلغ",
            }}
            renderer={{
              name: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {props}
                </Text>
              ),
              date: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {getDateStr(props)}
                </Text>
              ),
              number: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {props}
                </Text>
              ),
              status: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {chequeStatusToString(props)}
                </Text>
              ),
              price: ({ props }: TableRendererProps) => (
                <Text size={12} color={theme.textOnOverlay}>
                  {addComma(props)}
                </Text>
              ),
            }}
            layout={{
              name: 200,
              date: 150,
              number: 200,
              status: 120,
              price: 150,
            }}
            rowEvents={[
              {
                type: "click",
                handler: (e, data) => {
                  const sc: Cheque[] = findCheque(data);
                  if (!sc.length) return;
                  setPopupBody(
                    <ChequeDetails {...sc[0]} updateCb={onSearch} />
                  );
                },
              },
            ]}
          />
        </div>
      </Column>
    </>
  );
};

export default SearchCheque;
